import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearStoredToken,
  getApiBase,
  getStoredToken,
  health,
  setApiBase,
  setStoredToken,
} from '../api/client';

interface AuthState {
  token: string;
  apiBase: string;
  ready: boolean;
  setToken: (token: string) => void;
  setBase: (base: string) => void;
  logout: () => void;
  verify: () => Promise<boolean>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState(() => getStoredToken());
  const [apiBase, setApiBaseState] = useState(() => getApiBase());
  const [ready] = useState(true);

  const setToken = useCallback((t: string) => {
    setStoredToken(t);
    setTokenState(t);
  }, []);

  const setBase = useCallback((b: string) => {
    setApiBase(b);
    setApiBaseState(b.replace(/\/+$/, ''));
  }, []);

  const logout = useCallback(() => {
    clearStoredToken();
    setTokenState('');
  }, []);

  const verify = useCallback(async () => {
    try {
      await health();
      // health 公开；再测一个需鉴权接口
      if (!getStoredToken()) return false;
      const res = await fetch(`${getApiBase()}/api/mailboxes`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({
      token,
      apiBase,
      ready,
      setToken,
      setBase,
      logout,
      verify,
    }),
    [token, apiBase, ready, setToken, setBase, logout, verify],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
